import type { Board } from "./[boardId].page";
import { v4 as uuidv4 } from "uuid";
import {
  ApiOutlined,
  AppstoreAddOutlined,
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  EllipsisOutlined,
  FilterOutlined,
  FullscreenOutlined,
  InfoCircleOutlined,
  LinkOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import { Button, Drawer, Dropdown, Select, Tooltip, theme } from "antd";
import { ExploreCard } from "pages/api/deep-links/types";
import {
  PropsWithChildren,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from "react";
import { Flex } from "src/components/Flex";
import { SparkleIcon } from "src/components/SparkleIcon";
import { useSnippets } from "src/hooks/useSnippets";
import { SearchState } from "src/search/types";
import { mono } from "src/fonts";
import { asKey } from "src/utils/fields";
import { useHover } from "usehooks-ts";
import { useEntitySelectors } from "src/search/useEntitySelectors";
import { TextualQuery } from "../explore/TextualQuery";
import Link from "next/link";
import { useApplicationContext } from "src/hooks/useApplication";
import {
  ensureState,
  serializeParams,
} from "src/search/lib/params-state-reducer";
import { serializeContentView } from "../explore/lib/useContentView";
import { serializeDocument } from "../explore/lib/useExploreDocument";
import { serializeTabs } from "../explore/lib/useTabs";
import { useRouter } from "next/router";
import { usePopupContext } from "src/hooks/usePopup";
import { SnippetMenu } from "src/search/components/inputs/question-input/SnippetMenu";
import { getCorrectedContentView, mergeState } from "./lib";
import { cloneData } from "src/utils/objects";
import { ToasterMessageType, useToasterContext } from "src/hooks/useToaster";
import { createPortal } from "react-dom";
import { QuestionEditor } from "../dashboards/QuestionEditor";
import { downloadSearchStateAsCSV } from "src/search/components/inputs/question-input/Controls";
import styles from "./dashboard.module.css";
import { ChatAssistant } from "src/search/components/ChatAssistant";
import { ModalInsert } from "src/components/ModalInsert";
import { BoardAddForm } from "../explore/BoardAddForm";
import { DebugButton } from "src/components/DebugButton";
import { contextType } from "react-grid-layout";

export const TITLEBAR_HEIGHT = 24;

export function QuestionTitlebar(props: {
  board: Board;
  changed: boolean;
  onReset(): void;
  searchState: SearchState;
  setSearchState: (state: SearchState) => void;
  card: ExploreCard;
  cardId: string;
  pivotId?: string;
  setPivotId: (pivotId: string) => void;
  onArrange(): void;
}) {
  const [boardModal, setBoardModal] = useState<ReactElement>();
  const router = useRouter();
  const orgId = useApplicationContext().org_id;
  const toaster = useToasterContext();
  const [describing, setDescribing] = useState(false);
  const [mapping, setMapping] = useState(false);
  const [editingCard, setEditingCard] = useState(false);
  const [snippetting, setSnippetting] = useState<
    | React.KeyboardEvent<HTMLElement>
    | React.MouseEvent<HTMLElement, MouseEvent>
    | undefined
  >(undefined);
  const snippets = useSnippets({
    isForCard: false,
    onlyUserOwned: true,
  }).list;
  const variations = useSnippets({
    isForCard: true,
    isTagged: props.card.snippetTags,
  }).list;
  const hoverRef = useRef(null);
  const isHovering = useHover(hoverRef);
  const [modal, setModal] = useState<JSX.Element | null>(null);

  // secondary menus
  const popup = usePopupContext();
  const menuRefs = useRef({
    popup,
    searchState: props.searchState,
    applyState: props.setSearchState,
    stopSnippetting() {
      setSnippetting(undefined);
    },
  });
  menuRefs.current = {
    popup,
    searchState: props.searchState,
    applyState: props.setSearchState,
    stopSnippetting() {
      setSnippetting(undefined);
    },
  };

  // open the snippetting menu
  useEffect(() => {
    if (snippetting) {
      menuRefs.current.popup.setPopup(
        snippetting,
        <SnippetMenu
          snippets={snippets}
          onChange={(snippet) => {
            const ctx = { state: menuRefs.current.searchState };
            const state: Partial<SearchState> = mergeState(ctx.state, snippet);
            menuRefs.current.stopSnippetting();
            menuRefs.current.popup.close();
            menuRefs.current.applyState(state as SearchState);
          }}
        />
      );
    }
  }, [snippetting, snippets]);

  const { filters } = useEntitySelectors({ state: props.searchState });
  const items = filters.map((filter) => ({
    key: asKey(filter),
    label: asKey(filter),
    value: asKey(filter),
  }));
  items.unshift({
    key: "default",
    label: "Default Mapping",
    value: "default",
  });

  const app = useApplicationContext();
  return (
    <>
      <Flex className={`${styles.questionTitle} hover-show`}>
        {props.card.centerTitle && (
          <div className="hover-show-target" style={{ width: 34 }} />
        )}
        <div
          className={`${styles.questionTitleContent} ${
            props.card.centerTitle ? styles.centered : ""
          }`}
        >
          <div
            className={`${styles.questionTitleText} ${
              props.changed ? styles.changed : ""
            }`}
          >
            {props.card.showTitle &&
              ((props.card.snippetTags?.length || 0) > 0 &&
              variations.length > 0 ? (
                <Select
                  className="pendo-dashboard-question-snippets"
                  bordered={false}
                  options={[
                    {
                      value: "",
                      label: (
                        <span className={styles.questionTitleText}>
                          {props.card.title}
                        </span>
                      ),
                    },
                    ...variations.map((link) => ({
                      value: link.id,
                      label: (
                        <span className={styles.questionTitleText}>
                          {link.name}
                        </span>
                      ),
                    })),
                  ]}
                  popupMatchSelectWidth={false}
                  style={{
                    display: "inline-block",
                    maxWidth: "100%",
                  }}
                  value={props.pivotId}
                  onChange={(id) => props.setPivotId(id)}
                />
              ) : (
                <div className="trunc">{props.card.title}</div>
              ))}
          </div>
        </div>
        <div className="hover-show-target">
          {app.isSuperAdmin && (
            <>
              <Tooltip title="Result Insights">
                <Button
                  size="small"
                  shape="circle"
                  type="text"
                  style={{ paddingBottom: -2 }}
                  onClick={() => {
                    setModal(
                      <Drawer
                        title="Analysis"
                        placement="right"
                        onClose={() => setModal(null)}
                        open={true}
                        width="80%"
                      >
                        <ChatAssistant
                          title="Analysis"
                          initialPrompt={`Analyze this data`}
                          dataSources={[
                            { query: props.searchState, format: "csv" },
                          ]}
                          timeframe={{
                            duration: props.searchState.duration || "P7D",
                          }}
                          onClose={() => setModal(null)}
                          inline={true}
                        />
                      </Drawer>
                    );
                  }}
                >
                  <SparkleIcon size={12} />
                </Button>
              </Tooltip>
            </>
          )}
          <Dropdown
            className="pendo-dashboard-question-menu"
            trigger={["click"]}
            menu={{
              items: [
                {
                  key: "edit",
                  label: "Edit Card",
                  icon: <EditOutlined />,
                  disabled: !props.board.userCanEdit,
                  onClick() {
                    setEditingCard(true);
                  },
                },
                {
                  key: "explore",
                  label: (
                    <Link
                      href={{
                        pathname: "/[orgId]/explore",
                        query: {
                          orgId,
                          s: serializeParams(ensureState(props.searchState)),
                          v: serializeContentView(
                            getCorrectedContentView(
                              props.card.state.viz,
                              Boolean(props.pivotId)
                            )
                          ),
                          d: serializeDocument({
                            saved: false,
                            title: props.card.title,
                          }),
                          t: serializeTabs({}),
                        },
                      }}
                      onClick={(e) => {
                        // TODO maybe this should just be the normal HREF value
                        e.preventDefault();
                        const href = (e.target as HTMLAnchorElement | undefined)
                          ?.href;
                        if (href && orgId) {
                          const [_etc, ...pathAfterOrgId] = href.split(orgId);
                          const url = `/${orgId}${pathAfterOrgId.join(orgId)}`;
                          router.push({
                            pathname: "/[orgId]/t/new",
                            query: { orgId, url, title: props.card.title },
                          });
                        }
                      }}
                    >
                      Explore Results
                    </Link>
                  ),
                  icon: <LinkOutlined />,
                },
                {
                  key: "explain",
                  label: "Explain Query",
                  icon: <InfoCircleOutlined />,
                  onClick() {
                    setDescribing((v) => !v);
                  },
                },
                { type: "divider" },

                {
                  key: "rearrange",
                  label: "Move or Resize",
                  icon: <FullscreenOutlined />,
                  disabled: !props.board.userCanEdit,
                  onClick() {
                    props.onArrange();
                  },
                },
                {
                  key: "duplicate",
                  label: "Duplicate",
                  icon: <CopyOutlined />,
                  disabled: !props.board.userCanEdit,
                  onClick() {
                    if (!props.board.link) return;
                    const ogId = props.cardId;
                    const dupeId = uuidv4();
                    const dupe = {
                      [dupeId]: {
                        ...props.card,
                        title: `${props.card.title} - (COPY)`,
                      },
                    };

                    const ogLayout = props.board.link.json.layout.find(
                      (layout) => layout.i === ogId
                    )!;
                    const newLayouts = [
                      ...props.board.link.json.layout.map((layout) => ({
                        ...layout,
                        y: layout.y + (layout.y > ogLayout.y ? ogLayout.h : 0),
                      })),
                      {
                        ...ogLayout,
                        y: ogLayout.y + ogLayout.h,
                        i: dupeId,
                      },
                    ];
                    return props.board
                      .save({
                        id: props.board.link.id,
                        name: props.board.link.name,
                        description: props.board.link.description,
                        scope: props.board.link.userId
                          ? "private"
                          : props.board.link.orgId
                          ? "workspace"
                          : "global",
                        ...props.board.link.json,
                        data: {
                          ...props.board.link.json.data,
                          ...dupe,
                        },
                        layout: newLayouts,
                      })
                      .then(() => {
                        toaster.addMessage(ToasterMessageType.SUCCESS, {
                          message: "Duplicated Card",
                        });
                        setMapping(false);
                      });
                  },
                },
                {
                  key: "add-to-board",
                  label: "Add to Board",
                  icon: <AppstoreAddOutlined />,
                  onClick(info) {
                    setBoardModal(
                      <ModalInsert
                        onClose={() => setBoardModal(undefined)}
                        target={info.domEvent.currentTarget as HTMLElement}
                        width={380}
                        offset={{ x: -330, y: 10 }}
                      >
                        <BoardAddForm
                          title={props.card.title}
                          searchState={props.searchState}
                          viewValue={props.card.state.viz.value}
                          viewConfig={props.card.state.viz.config}
                          onClose={() => setBoardModal(undefined)}
                        />
                      </ModalInsert>
                    );
                  },
                },
                { type: "divider" },
                {
                  key: "export-csv",
                  label: <span style={{ fontSize: 13 }}>Export as csv</span>,
                  icon: <DownloadOutlined />,
                  onClick() {
                    downloadSearchStateAsCSV(props.searchState, orgId!);
                  },
                },
                {
                  key: "snippets",
                  disabled: snippets.length === 0,
                  label: "Apply Snippet",
                  icon: <FilterOutlined />,
                  onClick(info) {
                    setSnippetting(info.domEvent);
                  },
                },
                { type: "divider" },
                {
                  key: "reset",
                  label: "Reset Card Defaults",
                  disabled: !props.changed,
                  icon: <UndoOutlined />,
                  onClick() {
                    props.onReset();
                  },
                },
                {
                  key: "map",
                  label: "Map Board Filters",
                  icon: <ApiOutlined />,
                  disabled: !props.board.userCanEdit,
                  onClick() {
                    setMapping(true);
                  },
                },
                {
                  key: "delete",
                  label: "Remove Card",
                  icon: <DeleteOutlined />,
                  danger: true,
                  disabled: !props.board.userCanEdit,
                  onClick() {
                    if (!props.board.link) return;
                    const proceed = confirm(
                      "Do you want to delete this card from the board?"
                    );
                    if (proceed) {
                      const newData = { ...props.board.link.json.data };
                      delete newData[props.cardId];
                      const newLayout = props.board.link.json.layout.filter(
                        (layout) => layout.i !== props.cardId
                      );
                      return props.board
                        .save({
                          id: props.board.link.id,
                          name: props.board.link.name,
                          description: props.board.link.description,
                          scope: props.board.link.userId
                            ? "private"
                            : props.board.link.orgId
                            ? "workspace"
                            : "global",
                          ...props.board.link.json,
                          data: newData,
                          layout: newLayout,
                        })
                        .then(() => {
                          toaster.addMessage(ToasterMessageType.SUCCESS, {
                            message: "Removed card",
                          });
                          props.board.refresh();
                        });
                    }
                  },
                },
                { type: "divider" },
              ],
            }}
          >
            <Button size="small" shape="circle" type="text">
              <EllipsisOutlined />
            </Button>
          </Dropdown>
        </div>
      </Flex>
      <div ref={hoverRef}>
        <CardOverlay hovering={isHovering} enabled={mapping} onClick={() => {}}>
          <Flex direction="column" gap={16} style={{ height: "100%" }}>
            <div>
              <span style={{ fontSize: 13 }} className={mono.className}>
                Activity.Actor {"->"}
              </span>
              <Select
                showSearch
                style={{ width: 200, display: "block" }}
                options={items}
                filterOption={true}
                value={
                  props.card.mappings?.find(
                    (m) =>
                      m.type === "condition" &&
                      m.fromFieldKey ===
                        asKey({ target: "Activity", attribute: "Actor" })
                  )?.toFieldKey || "default"
                }
                loading={props.board.saving}
                onChange={async (mapKey) => {
                  const cardData = cloneData(props.card);
                  cardData.mappings =
                    !mapKey || mapKey === "default"
                      ? []
                      : [
                          {
                            type: "condition",
                            fromFieldKey: asKey({
                              target: "Activity",
                              attribute: "Actor",
                            }),
                            toFieldKey: mapKey,
                          },
                        ];
                  if (!props.board.link)
                    throw new Error("expected link to save");
                  return props.board
                    .save({
                      id: props.board.link.id,
                      name: props.board.link.name,
                      description: props.board.link.description,
                      scope: props.board.link.userId
                        ? "private"
                        : props.board.link.orgId
                        ? "workspace"
                        : "global",
                      ...props.board.link.json,
                      data: {
                        ...props.board.link.json.data,
                        [props.cardId]: cardData,
                      },
                    })
                    .then(() => {
                      toaster.addMessage(ToasterMessageType.SUCCESS, {
                        message: "Updated card mapping",
                      });
                      setMapping(false);
                    });
                }}
              />
            </div>
            <Button
              shape="round"
              size="small"
              onClick={() => setMapping(false)}
            >
              done
            </Button>
          </Flex>
        </CardOverlay>
        <CardOverlay
          hovering={isHovering}
          enabled={describing}
          onClick={() => setDescribing(false)}
        >
          <div
            style={{
              width: "100%",
              height: "100%",
              overflow: "auto",
              padding: 12,
            }}
          >
            <TextualQuery
              state={props.searchState}
              Val={({ children }) => (
                <span
                  className={mono.className}
                  style={{
                    fontWeight: 600,
                  }}
                >
                  {children}
                </span>
              )}
              Sep={() => <div style={{ height: 6 }} />}
            />
          </div>
        </CardOverlay>
      </div>
      {modal}
      {boardModal}
      {editingCard &&
        createPortal(
          <QuestionEditor
            card={props.card}
            updateCard={async (cardData) => {
              if (!props.board.link)
                throw new Error("expected board link to save");
              const update = { [props.cardId]: cardData };
              return props.board
                .save({
                  id: props.board.link.id,
                  name: props.board.link.name,
                  description: props.board.link.description,
                  scope: props.board.link.userId
                    ? "private"
                    : props.board.link.orgId
                    ? "workspace"
                    : "global",
                  ...props.board.link.json,
                  data: {
                    ...props.board.link.json.data,
                    ...update,
                  },
                })
                .then(() => {
                  toaster.addMessage(ToasterMessageType.SUCCESS, {
                    message: "Saved card and board",
                  });
                });
            }}
            onClose={() => {
              setEditingCard(false);
            }}
            okText="Save"
          />,
          document.querySelector("#screen-insert") || document.body
        )}
    </>
  );
}

export const CardOverlay = ({
  children,
  onClick,
  enabled,
  hovering,
}: PropsWithChildren<{
  enabled: Boolean;
  hovering: Boolean;
  onClick(): void;
}>) => {
  const { token } = theme.useToken();
  return (
    <div
      className={`${styles.questionOverlay} ${
        hovering ? styles.hovering : styles.default
      }`}
      style={{ display: enabled ? "block" : "none" }}
      onClick={onClick}
    >
      {children}
    </div>
  );
};
